import { RouteCodec } from 'app/constants/Routes/codecs';
import IRoute, { IRouteInit } from 'app/constants/Routes/IRoute';
import { decodeOrThrow } from 'app/utils/ioTs';
import { isSafelyAppendablePathname } from 'app/utils/routes';
import { getBasePath } from 'app/services/basePath';
import { isRight } from 'fp-ts/lib/Either';
import { compile, Key, pathToRegexp } from 'path-to-regexp';

interface IDynamicRouteInit<Params> extends IRouteInit {
  codec: RouteCodec<Params>;
}

export type ParamsBase = { [index: string]: unknown };
export interface IDynamicRoute<Params extends ParamsBase> extends IRoute {
  getPathname(params: Params): string;
}

/**
 * Routes that offer type guards for creating pathnames.
 */
class DynamicRoute<Params extends { [index: string]: unknown }> implements IDynamicRoute<Params> {
  public readonly syncable: boolean;

  private readonly codec: RouteCodec<Params>;
  private readonly keys: string[];
  private matcherInternal: [string, RegExp];
  private readonly route: string;

  public constructor({ route, syncable, codec }: IDynamicRouteInit<Params>) {
    this.route = route;

    if (!isSafelyAppendablePathname(this.route)) {
      throw new Error(`${this.route} is not a valid pathname.`);
    }

    this.syncable = syncable;
    this.codec = codec;
    const keyDetails: Key[] = [];
    this.matcherInternal = [this.route, pathToRegexp(this.route, keyDetails)];

    if (keyDetails.length === 0) {
      throw new Error(`${route} does not appear to be dynamic.`);
    }

    // Extract the name from the keys and just create a list of the keys in order.
    this.keys = keyDetails.map(({ name }) => `${name}`);
  }

  private get compiler() {
    return compile(this.prefixedRoute);
  }

  private get prefixedRoute() {
    return `${getBasePath()}${this.route}`;
  }

  private get matcher() {
    if (this.prefixedRoute !== this.matcherInternal[0]) {
      const keyDetails: Key[] = [];
      this.matcherInternal = [this.prefixedRoute, pathToRegexp(this.prefixedRoute, keyDetails)];
      if (keyDetails.length === 0) {
        throw new Error(`${this.prefixedRoute} does not appear to be dynamic.`);
      }
    }
    return this.matcherInternal[1];
  }

  /**
   * Decodes the parameters from a matching pathname.
   */
  public decode(pathname: string): Params {
    const { codec, keys, matcher } = this;
    const values = (matcher.exec(pathname) || []).slice(1);

    // Copy the content into another array.
    const params: { [index: string]: unknown } = {};

    keys.forEach((key, index) => {
      params[key] = values[index];
    });

    return decodeOrThrow(codec, params);
  }

  /**
   * Encodes parameters into the pathname.
   */
  public getPathname(params: Params): string {
    const { codec, compiler } = this;
    return compiler(codec.encode(params));
  }

  public get id() {
    return this.route;
  }

  /**
   * Determines if a pathname matches this route.
   */
  public match(pathname: string) {
    const { codec, keys, matcher } = this;
    const matches = matcher.exec(pathname);

    if (matches === null) {
      return false;
    }

    const params: { [index: string]: unknown } = {};

    // Verify that each value meets the constraints of the codec.
    matches.slice(1).forEach((value, i) => {
      const key = keys[i];
      params[key] = value;
    });

    // If the params can be decoded, then they are valid.
    return isRight(codec.decode(params));
  }

  /**
   * Typeguard to validate if params can work on a route.
   */
  public validParams(args: unknown): args is Params {
    return this.codec.is(args);
  }
}

export default DynamicRoute;
